import { ConfigProvider, Form } from 'antd';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { ApiCaller, Colors, Utils } from '@config';
import ButtonLoader from '@components/Button/ButtonLoader';
import { IoChevronBack } from 'react-icons/io5';
import { FaArrowRight } from 'react-icons/fa6';
import { zodResolver } from '@hookform/resolvers/zod';
import useColors from '@hooks/useColors';
import { companyOnboardingSchema } from '@config/Schema';
import { CompanyOnboardingAction } from '@store/actions';
import { companyOnboardingDefaults, companyOnboardingSteps } from '../helper';
import useSteps from '@hooks/useSteps';
import { MainTitle } from '@components/ui/typography';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Text, TextInput } from '../../../components';
import { verifyCompany } from '../../../config/getApis';
import { AppAction } from '../../../store/actions';
import { useNavigate } from 'react-router';

const CompanyOnboarding = () => {
    const [isValidating, setIsValidating] = useState(false);
    const [companyUrlModal, setCompanyUrlModal] = useState({
        isOpen: false,
        loading: false,
        data: null,
    });

    const dispatch = useDispatch();
    const loader = useSelector(
        (state) => state?.CompanyOnboardingReducer.loader,
    );

    // just for now we are getting subscriptions here to set the id statically.
    const subscriptions = useSelector(
        (store) => store.SubscriptionsReducer.data,
    );

    const { colorPrimary } = useColors();
    const { current, isLastStep, next, prev } = useSteps(3);

    const formMethods = useForm({
        resolver: yupResolver(companyOnboardingSchema[current]),
        defaultValues: companyOnboardingDefaults,
        mode: 'onChange',
    });

    const { handleSubmit, getValues } = formMethods;

    const navigate = useNavigate();

    const handleOnboard = async (data) => {
        let isValid = true;
        setIsValidating(true);

        try {
            await companyOnboardingSteps?.[current]?.validate(data);
            console.log('Step 2');
            isValid = true;
        } catch (err) {
            setIsValidating(false);
            Utils.showMessage('error', err?.response?.data?.message);
            isValid = false;
        }

        if (isValid) {
            if (isLastStep) {
                const allData = getValues();
                // upload logo
                const formData = new FormData();
                formData.append('file', allData.files[0]);
                formData.append('type', 'profile');

                try {
                    const res = await ApiCaller.Post('/media/upload', formData);
                    if (res?.status == 200) {
                        const transformedData = {
                            ...allData,
                            password: allData.passwords.password,
                            primaryColour: allData.colors.primaryColour,
                            secondaryColour: allData.colors.secondaryColour,
                            logoId: res.data?.data?.id,
                            // setting it statically for now.
                            subscriptionId:
                                subscriptions?.length > 0
                                    ? subscriptions[0]?.id
                                    : 1,
                        };

                        delete transformedData.passwords;
                        delete transformedData.files;
                        delete transformedData.colors;

                        // submit form
                        dispatch(
                            CompanyOnboardingAction.OnboardCompany(
                                transformedData,
                            ),
                        );
                    }
                } catch (err) {
                    console.log('image error: ', err);
                    Utils.showMessage('error', err?.response?.data?.message);
                    setIsValidating(false);
                }

                // end after submission
                return;
            }

            next();
            setIsValidating(false);
        }
    };

    const items = useMemo(
        () =>
            companyOnboardingSteps.map((item) => ({
                key: item.title,
                title: item.title,
            })),
        [companyOnboardingSteps],
    );

    const loginRedirectHandler = () => {
        setCompanyUrlModal({
            ...companyUrlModal,
            isOpen: true,
        });
    };

    const isCompanyValidHandler = async () => {
        if (companyUrlModal?.data?.length == 0) return;
        else {
            setCompanyUrlModal({ ...companyUrlModal, loading: true });
            const result = await verifyCompany(companyUrlModal?.data);
            if (result instanceof Error) {
                // handle failure
                Utils.showMessage('error', result?.response?.data?.message);
                setCompanyUrlModal({ ...companyUrlModal, loading: false });
            } else {
                // handle success
                dispatch(AppAction.ValidCompany(result));
                localStorage.setItem('companyData', JSON.stringify(result));
                setCompanyUrlModal({
                    ...companyUrlModal,
                    isOpen: false,
                    data: null,
                    loading: false,
                });
                window.location.replace(Utils.getTenantLoginUrl(result?.url));
                // to run live
                // window.location.replace(
                //     `https://${result?.url}.guardsconnect.com/login`,
                // );

                // QA/STAGING Build
                // window.location.replace(
                //     `https://${result?.url}.guardsconnect.5stardesigners.net/qa/login`,
                // );
            }
        }
    };

    return (
        <>
            {current > 0 && (
                <button
                    onClick={prev}
                    className="border-none cursor-pointer bg-transparent absolute flex items-center gap-1 top-[10px] left-[10px]"
                >
                    <IoChevronBack /> Back
                </button>
            )}

            <FormProvider {...formMethods}>
                <MainTitle className="text-primary">
                    Try Free For 30 Days
                </MainTitle>
                <p
                    className="text-center mb-5 max-w-[38ch]"
                    style={{ color: Colors.Gray }}
                >
                    We've helped over 600 security companies streamline their
                    operations.
                </p>
                <ConfigProvider
                    theme={{
                        components: {
                            Steps: {
                                lineWidth: 5,
                                iconTop: -4,
                            },
                        },
                    }}
                >
                    <div className="flex items-center gap-2 w-full max-w-[300px]">
                        {items.map((item, idx) => {
                            const color =
                                idx > current ? '#F3F6FF' : colorPrimary;
                            return (
                                <React.Fragment key={item.title}>
                                    {idx > 0 && (
                                        <div
                                            className="h-1.5 rounded-full flex-1 duration-300"
                                            style={{ backgroundColor: color }}
                                        ></div>
                                    )}
                                    <div
                                        className="w-[25px] h-[25px] border-[4px] rounded-full flex items-center justify-center duration-300 text-xs"
                                        style={{ borderColor: color }}
                                    >
                                        {item.title}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </ConfigProvider>

                <div className="w-full md:mt-0 sm:max-w-md xl:p-0">
                    <div className="pt-3 mx-auto flex items-center flex-col justify-center">
                        <Form
                            onFinish={handleSubmit(handleOnboard)}
                            className="space-y-4 w-full"
                        >
                            {companyOnboardingSteps[current].content}
                            <ButtonLoader
                                style={{ backgroundColor: colorPrimary }}
                                icon={<FaArrowRight size={14} />}
                                iconPosition="end"
                                loader={loader || isValidating}
                                text={
                                    isLastStep
                                        ? 'Start Free Trial'
                                        : 'Next Step'
                                }
                                className="cursor-pointer mx-auto focus:ring-4 focus:outline-none font-medium rounded-full text-center h-12 px-8 text-base tracking-wider flex items-center"
                            />
                        </Form>

                        <p className="flex items-center justify-end text-sm font-medium text-primary-600 mt-3">
                            Already have an account?{' '}
                            <span
                                className="ml-1 cursor-pointer"
                                style={{ color: colorPrimary }}
                                onClick={loginRedirectHandler}
                            >
                                Login
                            </span>
                        </p>

                        <p className="text-xs text-center mt-6 font-semibold">
                            Copyright 2025{' '}
                            <span style={{ color: colorPrimary }}>
                                GuardsConnect
                            </span>{' '}
                            Tier Security, Inc
                        </p>
                    </div>
                </div>
            </FormProvider>

            <Modal.Basic
                open={companyUrlModal.isOpen}
                width={350}
                onCancel={() =>
                    setCompanyUrlModal({
                        ...companyUrlModal,
                        isOpen: false,
                        data: null,
                    })
                }
                title=""
                Children={
                    <div className="flex flex-col gap-5 items-center justify-center">
                        <h3 className="text-lg font-semibold">
                            Enter Company Name
                        </h3>
                        <TextInput
                            value={companyUrlModal?.data}
                            onChange={(e) =>
                                setCompanyUrlModal({
                                    ...companyUrlModal,
                                    data: e?.target.value,
                                })
                            }
                            fieldClasses={'rounded-lg'}
                        />
                        <ButtonLoader
                            style={{ backgroundColor: colorPrimary }}
                            loader={companyUrlModal?.loading}
                            text={'Save'}
                            btnHandler={isCompanyValidHandler}
                            className="cursor-pointer mx-auto focus:ring-4 focus:outline-none font-medium rounded-md text-center h-10 px-8 text-base tracking-wider flex items-center"
                        />
                    </div>
                }
            />
        </>
    );
};

export default CompanyOnboarding;
